import React, { type CSSProperties, type ReactNode } from 'react';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AddIcon from '@mui/icons-material/Add';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssistantIcon from '@mui/icons-material/Assistant';
import BookIcon from '@mui/icons-material/Book';
import BuildIcon from '@mui/icons-material/Build';
import BusinessIcon from '@mui/icons-material/Business';
import CampaignIcon from '@mui/icons-material/Campaign';
import ChatIcon from '@mui/icons-material/Chat';
import CloudIcon from '@mui/icons-material/Cloud';
import CodeIcon from '@mui/icons-material/Code';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import GridOnIcon from '@mui/icons-material/GridOn';
import HandymanIcon from '@mui/icons-material/Handyman';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import HelpIcon from '@mui/icons-material/Help';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HubIcon from '@mui/icons-material/Hub';
import ImageIcon from '@mui/icons-material/Image';
import InventoryIcon from '@mui/icons-material/Inventory';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import MessageIcon from '@mui/icons-material/Message';
import PaletteIcon from '@mui/icons-material/Palette';
import PeopleIcon from '@mui/icons-material/People';
import RefreshIcon from '@mui/icons-material/Refresh';
import RobotIcon from '@mui/icons-material/SmartToy';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SpeedIcon from '@mui/icons-material/Speed';
import StoreIcon from '@mui/icons-material/Store';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import type { SvgIconProps } from '@mui/material/SvgIcon';

type IconComponentType = React.ComponentType<SvgIconProps>;
type IconSize = number | 'sm' | 'md' | 'lg' | 'xl' | 'small' | 'medium' | 'large';

// Icon mapping for easy reference
const iconMap: Record<string, IconComponentType> = {
  // Chat and communication
  chat: ChatIcon,
  message: MessageIcon,
  add: AddIcon,
  delete: DeleteIcon,
  clear: DeleteIcon,

  // Agents and AI
  robot: RobotIcon,
  agent: RobotIcon,
  assistant: AssistantIcon,
  support: HeadsetMicIcon,
  search: SearchIcon,
  development: SearchIcon,
  edit: EditIcon,
  creative: EditIcon,
  analytics: AnalyticsIcon,
  chatbot: RobotIcon,

  // Dashboards and data
  dashboard: DashboardIcon,
  chart: TrendingUpIcon,

  // Documentation and settings
  documentation: BookIcon,
  book: BookIcon,
  settings: SettingsIcon,
  config: SettingsIcon,
  tools: HandymanIcon,
  build: BuildIcon,
  gear: SettingsIcon,
  wrench: BuildIcon,

  // Status and actions
  loading: HourglassEmptyIcon,
  error: ErrorIcon,
  refresh: RefreshIcon,
  expand: ExpandMoreIcon,
  collapse: ExpandLessIcon,

  // File and content
  file: FolderIcon,
  folder: FolderIcon,
  description: DescriptionIcon,
  code: CodeIcon,
  palette: PaletteIcon,

  // Help and support
  help: HelpIcon,

  // Ad Manager
  'ad-manager': CampaignIcon,
  campaign: CampaignIcon,
  ads: CampaignIcon,
  'shopping-cart': ShoppingCartIcon,

  // Grid and flow
  grid: GridOnIcon,
  flow: AccountTreeIcon,

  // Administration
  admin: AdminPanelSettingsIcon,
  administration: AdminPanelSettingsIcon,
  account: BusinessIcon,
  accounts: BusinessIcon,
  user: PeopleIcon,
  users: PeopleIcon,

  // Actions
  'arrow-back': ArrowBackIcon,
  save: SaveIcon,

  // Media and inventory
  store: StoreIcon,
  image: ImageIcon,
  inventory: InventoryIcon,
  cloud: CloudIcon,
  speed: SpeedIcon,
  hub: HubIcon,
  package: InventoryIcon,
  logout: LogoutIcon,
  roles: ManageAccountsIcon,
  policies: SecurityIcon,
  security: SecurityIcon,

  // Default fallback
  default: RobotIcon,
};

type IconProps = Omit<SvgIconProps, 'children' | 'color' | 'fontSize'> & {
  name?: string;
  size?: IconSize;
  color?: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

const Icon = ({
  name,
  size = 'medium',
  color = 'inherit',
  className = '',
  style = {},
  children: _children, // Explicitly extract and ignore children
  ...props
}: IconProps) => {
  const IconComponent = iconMap[name] || iconMap.default;

  const sizeMap: Record<string, number> = {
    sm: 16,
    small: 16,
    md: 24,
    medium: 24,
    lg: 32,
    large: 32,
    xl: 48,
  };

  const iconSize = typeof size === 'number' ? size : sizeMap[size] || sizeMap.medium;

  // MUI icons don't accept children, so we explicitly exclude it
  return (
    <IconComponent
      style={{
        fontSize: iconSize,
        color,
        ...style,
      }}
      className={className}
      {...props}
    />
  );
};

export default Icon;
export { iconMap };
